import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, TrendingDown, Copy, UserPlus, UserMinus, 
  Trophy, Target, Activity, Percent, Clock, DollarSign, Shield,
  ChevronUp, ChevronDown, Star, Zap, Upload, FileText, Image, Trash2, Download
} from 'lucide-react';

import { API } from '@/config/api';

export default function CopyTrading() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [following, setFollowing] = useState([]);
  const [copyHistory, setCopyHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [followModalOpen, setFollowModalOpen] = useState(false);
  const [allocation, setAllocation] = useState([10]);
  const [activeTab, setActiveTab] = useState('leaderboard');
  
  // Copy trades upload state
  const [uploadedTrades, setUploadedTrades] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchUploadedTrades();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadRes, followRes, histRes] = await Promise.all([
        fetch(`${API}/copy-trading/leaderboard`),
        fetch(`${API}/copy-trading/following`, { credentials: 'include' }),
        fetch(`${API}/copy-trading/history`, { credentials: 'include' })
      ]);

      if (leadRes.ok) {
        const data = await leadRes.json();
        setLeaderboard(data);
      }
      if (followRes.ok) {
        const data = await followRes.json();
        setFollowing(data);
      }
      if (histRes.ok) {
        const data = await histRes.json();
        setCopyHistory(data);
      }
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  };

  const fetchUploadedTrades = async () => {
    try {
      const res = await fetch(`${API}/copy-trading/uploads`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUploadedTrades(data);
      }
    } catch (e) {
      console.error('Failed to fetch uploaded trades:', e);
    }
  };

  const handleTradeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'text/csv', 'application/pdf'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.csv', '.pdf'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast.error('Please upload a screenshot (PNG, JPG), CSV file, or PDF');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API}/copy-trading/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Trade uploaded successfully!');
        setUploadedTrades(prev => [data.trade, ...prev]);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to upload trade');
      }
    } catch (e) {
      toast.error('Failed to upload trade');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteUpload = async (tradeId) => {
    try {
      const res = await fetch(`${API}/copy-trading/uploads/${tradeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Trade deleted');
        setUploadedTrades(prev => prev.filter(t => t.trade_id !== tradeId));
      } else {
        toast.error('Failed to delete trade');
      }
    } catch (e) {
      toast.error('Failed to delete trade');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return Image;
    if (ext === 'csv') return FileText;
    return FileText;
  };

  const handleFollow = async () => {
    if (!selectedTrader) return;
    
    try {
      const res = await fetch(`${API}/copy-trading/follow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          trader_id: selectedTrader.id,
          allocation_percent: allocation[0]
        })
      });

      if (res.ok) {
        toast.success(`Now copying ${selectedTrader.name}'s trades!`);
        setFollowModalOpen(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to follow trader');
      }
    } catch (e) {
      toast.error('Failed to follow trader');
    }
  };

  const handleUnfollow = async (traderId) => {
    try {
      const res = await fetch(`${API}/copy-trading/unfollow/${traderId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Unfollowed trader');
        fetchData();
      }
    } catch (e) {
      toast.error('Failed to unfollow');
    }
  };

  const isFollowing = (traderId) => {
    return following.some(f => f.trader_id === traderId);
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low': return 'text-emerald-400 bg-emerald-500/20';
      case 'Medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'High': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Card className="bg-[#14120f] border-[#1c1814] p-8 text-center">
          <Users className="h-16 w-16 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Copy Trading</h2>
          <p className="text-gray-400 mb-4">Sign in to copy successful traders</p>
          <Button onClick={() => window.location.href = '/'} className="bg-[#D4AF37] text-black">
            Sign In
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#14120f] to-[#1c1814] border-b border-[#1c1814]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Copy className="h-8 w-8 text-[#D4AF37]" />
            <h1 className="text-3xl font-bold">Copy Trading</h1>
          </div>
          <p className="text-gray-400">Follow top traders and automatically copy their trades</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Following</p>
                  <p className="text-2xl font-bold text-white">{following.length}</p>
                </div>
                <Users className="h-8 w-8 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Copied Trades</p>
                  <p className="text-2xl font-bold text-white">{copyHistory.length}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Copy P&L</p>
                  <p className="text-2xl font-bold text-emerald-400">+$0.00</p>
                </div>
                <TrendingUp className="h-8 w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Win Rate</p>
                  <p className="text-2xl font-bold text-white">0%</p>
                </div>
                <Target className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[#14120f] border border-[#1c1814]">
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Trophy className="h-4 w-4 mr-2" /> Leaderboard
            </TabsTrigger>
            <TabsTrigger value="following" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Users className="h-4 w-4 mr-2" /> Following ({following.length})
            </TabsTrigger>
            <TabsTrigger value="uploads" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Upload className="h-4 w-4 mr-2" /> My Trades ({uploadedTrades.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Clock className="h-4 w-4 mr-2" /> History
            </TabsTrigger>
          </TabsList>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading traders...</div>
            ) : (
              <div className="grid gap-4">
                {leaderboard.map((trader, index) => (
                  <Card key={trader.id} className="bg-[#14120f] border-[#1c1814] hover:border-[#D4AF37]/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Rank */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                            index === 1 ? 'bg-gray-400/20 text-gray-300' :
                            index === 2 ? 'bg-orange-500/20 text-orange-400' :
                            'bg-[#1c1814] text-gray-400'
                          }`}>
                            {index + 1}
                          </div>
                          
                          {/* Avatar & Name */}
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#1c1814] flex items-center justify-center text-2xl">
                              {trader.avatar}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white">{trader.name}</span>
                                {index < 3 && <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />}
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Badge variant="outline" className="text-xs border-gray-700 text-gray-400">
                                  {trader.specialty}
                                </Badge>
                                <span className="text-gray-500">{trader.followers.toLocaleString()} followers</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Win Rate</p>
                            <p className="font-bold text-white">{trader.win_rate.toFixed(1)}%</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Total Return</p>
                            <p className={`font-bold ${trader.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {trader.total_return >= 0 ? '+' : ''}{trader.total_return.toFixed(1)}%
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Today</p>
                            <p className={`font-bold ${trader.today_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {trader.today_pnl >= 0 ? '+' : ''}{trader.today_pnl.toFixed(2)}%
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Risk</p>
                            <Badge className={getRiskColor(trader.risk_score)}>{trader.risk_score}</Badge>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Trades</p>
                            <p className="font-bold text-white">{trader.trades.toLocaleString()}</p>
                          </div>
                          
                          {/* Action Button */}
                          {isFollowing(trader.id) ? (
                            <Button 
                              variant="outline" 
                              onClick={() => handleUnfollow(trader.id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                            >
                              <UserMinus className="h-4 w-4 mr-1" /> Unfollow
                            </Button>
                          ) : (
                            <Button 
                              onClick={() => { setSelectedTrader(trader); setFollowModalOpen(true); }}
                              className="bg-[#D4AF37] text-black hover:bg-[#c9a432]"
                            >
                              <UserPlus className="h-4 w-4 mr-1" /> Copy
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Following Tab */}
          <TabsContent value="following" className="space-y-4">
            {following.length === 0 ? (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-12 text-center">
                  <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Not Following Anyone</h3>
                  <p className="text-gray-400 mb-4">Browse the leaderboard and follow traders to copy their trades</p>
                  <Button onClick={() => setActiveTab('leaderboard')} className="bg-[#D4AF37] text-black">
                    View Leaderboard
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {following.map(follow => {
                  const trader = leaderboard.find(t => t.id === follow.trader_id);
                  return (
                    <Card key={follow.trader_id} className="bg-[#14120f] border-[#1c1814]">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-[#1c1814] flex items-center justify-center text-2xl">
                              {trader?.avatar || '👤'}
                            </div>
                            <div>
                              <p className="font-bold text-white">{trader?.name || 'Trader'}</p>
                              <p className="text-sm text-gray-400">Following since {new Date(follow.followed_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Allocation</p>
                              <p className="font-bold text-[#D4AF37]">{follow.allocation_percent}%</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Copied Trades</p>
                              <p className="font-bold text-white">{follow.total_copied_trades}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">P&L</p>
                              <p className={`font-bold ${follow.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                ${follow.total_pnl.toFixed(2)}
                              </p>
                            </div>
                            <Button 
                              variant="outline" 
                              onClick={() => handleUnfollow(follow.trader_id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                            >
                              <UserMinus className="h-4 w-4 mr-1" /> Stop Copying
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Uploads Tab - Upload Copy Trades */}
          <TabsContent value="uploads" className="space-y-4">
            {/* Upload Section */}
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Upload className="h-5 w-5 text-[#D4AF37]" />
                  Upload Your Trades
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Share your trading results by uploading screenshots, CSV trade exports, or PDF statements.
                </p>
                <div 
                  className="border-2 border-dashed border-[#D4AF37]/30 rounded-lg p-8 text-center hover:border-[#D4AF37]/50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleTradeUpload}
                    className="hidden"
                    accept=".png,.jpg,.jpeg,.gif,.csv,.pdf"
                  />
                  <Upload className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
                  <p className="text-white font-medium mb-2">
                    {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-gray-500 text-sm">
                    PNG, JPG, CSV, or PDF (max 10MB)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Uploaded Trades List */}
            {uploadedTrades.length === 0 ? (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-12 text-center">
                  <FileText className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Trades Uploaded</h3>
                  <p className="text-gray-400">Upload your trade screenshots or exports to track your performance</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {uploadedTrades.map((trade) => {
                  const FileIcon = getFileIcon(trade.filename);
                  return (
                    <Card key={trade.trade_id} className="bg-[#14120f] border-[#1c1814]">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-[#D4AF37]/20 rounded-lg">
                              <FileIcon className="h-6 w-6 text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{trade.filename}</p>
                              <p className="text-gray-500 text-sm">
                                {trade.size} • Uploaded {new Date(trade.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-gray-700 text-gray-400 hover:text-white"
                              onClick={() => window.open(`${API}/copy-trading/uploads/${trade.trade_id}/download`, '_blank')}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                              onClick={() => handleDeleteUpload(trade.trade_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            {copyHistory.length === 0 ? (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-12 text-center">
                  <Clock className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Copy History</h3>
                  <p className="text-gray-400">Your copied trades will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-[#0a0a0f]">
                      <tr className="text-gray-400 text-sm">
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Trader</th>
                        <th className="px-4 py-3 text-left">Asset</th>
                        <th className="px-4 py-3 text-left">Side</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-right">P&L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {copyHistory.map(trade => (
                        <tr key={trade._id} className="border-t border-[#1c1814]">
                          <td className="px-4 py-3 text-gray-400">{new Date(trade.timestamp).toLocaleString()}</td>
                          <td className="px-4 py-3 text-white">{trade.trader_name}</td>
                          <td className="px-4 py-3 text-white">{trade.asset}</td>
                          <td className="px-4 py-3">
                            <Badge className={trade.side === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                              {trade.side}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right text-white">${trade.amount}</td>
                          <td className={`px-4 py-3 text-right ${trade.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {trade.pnl >= 0 ? '+' : ''}${trade.pnl}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Follow Modal */}
      <Dialog open={followModalOpen} onOpenChange={setFollowModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Copy className="h-5 w-5 text-[#D4AF37]" />
              Copy {selectedTrader?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Set your allocation to automatically copy this trader's trades
            </DialogDescription>
          </DialogHeader>
          
          {selectedTrader && (
            <div className="space-y-6 py-4">
              {/* Trader Stats */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-[#0a0a0f] rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-gray-400">Win Rate</p>
                  <p className="text-xl font-bold text-white">{selectedTrader.win_rate.toFixed(1)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-400">Total Return</p>
                  <p className="text-xl font-bold text-emerald-400">+{selectedTrader.total_return.toFixed(1)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-400">Risk Level</p>
                  <Badge className={getRiskColor(selectedTrader.risk_score)}>{selectedTrader.risk_score}</Badge>
                </div>
              </div>

              {/* Allocation Slider */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm text-gray-400">Portfolio Allocation</label>
                  <span className="text-[#D4AF37] font-bold">{allocation[0]}%</span>
                </div>
                <Slider
                  value={allocation}
                  onValueChange={setAllocation}
                  max={50}
                  min={5}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  {allocation[0]}% of your portfolio will be used to copy trades from this trader
                </p>
              </div>

              {/* Warning */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Shield className="h-5 w-5 text-yellow-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-400 font-medium">Risk Warning</p>
                    <p className="text-xs text-gray-400">
                      Past performance is not indicative of future results. Only invest what you can afford to lose.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleFollow} className="bg-[#D4AF37] text-black">
              <Zap className="h-4 w-4 mr-1" /> Start Copying
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
